import {EventLog} from '@cypro/engine/schema'
import type {LogItem, LogValue} from '@cypro/engine/utils'
import {
  getDatetimeFormatter,
  getNumberFormatter,
  getShortDateFormatter,
  multipleDigitsNumberFormatter,
} from '@cypro/ui/utils'
import type {TFnType} from '@tolgee/react'
import {getLogEvent} from './functions'
import {MESSAGE_DEFAULTS} from './message-defaults'

/** Events where stored items replace the old template (the template repeats the same values). */
const ITEMS_ONLY = new Set(['PATIENT_CHARACTERISTICS_UPDATED', 'SETTINGS_UPDATED'])

/** Existing detail keys shown as extra lines when present. Works for old logs too. */
const SHOWN_DETAIL_KEYS: Record<string, string[]> = {
  PATIENT_PROTOCOL_STOPPED: ['reason'],
  PATIENT_PROTOCOL_CYCLES_UPDATED: ['movedCycles', 'addedCycles', 'deletedCycles', 'reason'],
  PATIENT_TREATMENT_STOPPED: ['stoppedProtocols', 'reason'],
  PATIENT_THERAPY_SEQUENCE_PROTOCOL_UPDATED: ['reason'],
  PATIENT_THERAPY_SEQUENCE_PROTOCOL_DELETED: ['reason'],
  PATIENT_THERAPY_SEQUENCE_ADDED: ['therapyDate', 'time'],
  PRODUCTION_STEP_COMPLETED: ['stepName'],
  PRODUCTION_ABORTED: ['reason', 'remake'],
  PRODUCTION_SPOILED: ['reason', 'remake'],
  FAILED_LOGIN_ATTEMPT: ['loginRefusal'],
  REPORT_EXPORTED: ['identifiers'],
  REPORT_EXPORT_QUEUED: ['identifiers'],
}

/** How a shown detail value is formatted. A reason without a key shows as typed. */
const DETAIL_FORMAT: Record<string, Partial<LogItem>> = {
  therapyDate: {type: 'date'},
  stepName: {valueKey: 'event.value.step.{value}'},
  remake: {type: 'bool'},
  reason: {valueKey: 'abort_reason.{value}'},
  loginRefusal: {valueKey: 'event.value.login_refusal.{value}'},
  identifiers: {valueKey: 'event.value.{value}'},
}

const MAX_TEXT = 80

/** Placeholders older message texts use without listing them; a missing one would make ICU throw. */
const COMMON_DEFAULTS = ['name', 'code', 'id']

// `items` gets one badge per line; `variant` is already in the log name. A report export's column
// keys and filter JSON are stored for the audit trail but are not readable as badges.
const DRAWER_SKIPPED_KEYS = new Set(['reason', 'items', 'variant', 'columns', 'filters'])
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Detail entries the log drawer shows as badges. Internal ids are left out unless `showsName` says
 * the drawer resolves that key to a name (a user, patient, drug), and a value already shown once
 * (the lot stored as both batchId and lotNumber) is not repeated.
 */
export function drawerDetails(details: EventLog['details'], showsName: (key: string) => boolean) {
  const seen = new Set<string>()
  return Object.entries(details ?? {}).filter(([key, value]) => {
    if (DRAWER_SKIPPED_KEYS.has(key) || value === undefined || value === null || value === '') return false
    if (typeof value === 'string' && UUID.test(value) && !showsName(key)) return false
    const shown = String(value)
    if (seen.has(shown)) return false
    seen.add(shown)
    return true
  })
}

export function parseLogItems(details: EventLog['details']): LogItem[] {
  const raw = details?.items
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function shownDetailItems(key: string, details: EventLog['details']): LogItem[] {
  return (SHOWN_DETAIL_KEYS[key] ?? []).flatMap(field => {
    const value = details?.[field] as LogValue | undefined
    if (value === undefined || value === null || value === '') return []
    return [{field, value, ...DETAIL_FORMAT[field]}]
  })
}

function formatValue(item: LogItem, value: LogValue | undefined, t: TFnType): string {
  if (value === undefined || value === null || value === '') return '-'
  if (item.type === 'bool') return t(`event.value.${String(value)}`)
  if (item.type === 'date' || item.type === 'datetime') {
    // epoch milliseconds, or an ISO string for timestamps stored as text
    const ms =
      typeof value === 'number' ? value : Number.isNaN(Number(value)) ? Date.parse(String(value)) : Number(value)
    if (Number.isNaN(ms)) return String(value)
    return (item.type === 'date' ? getShortDateFormatter() : getDatetimeFormatter()).format(ms)
  }
  if (item.valueKey) {
    const raw = String(value)
    return t({key: item.valueKey.replace('{value}', raw), defaultValue: raw, ns: item.valueNs})
  }
  if (typeof value === 'number') {
    return item.decimals === undefined
      ? multipleDigitsNumberFormatter.format(value)
      : getNumberFormatter({maximumFractionDigits: item.decimals}).format(value)
  }
  const text = String(value)
  return text.length > MAX_TEXT ? `${text.slice(0, MAX_TEXT)}…` : text
}

export function formatLogItem(item: LogItem, t: TFnType): string {
  const label =
    item.label ?? (item.labelKey ? t({key: item.labelKey, defaultValue: item.field}) : t(`event.field.${item.field}`))
  const unit = item.unit ? ` ${item.unit}` : ''
  const isChange = 'old' in item || 'new' in item
  if (isChange && item.old !== null && item.old !== undefined) {
    return `${label}: ${formatValue(item, item.old, t)} → ${formatValue(item, item.new, t)}${unit}`
  }
  return `${label}: ${formatValue(item, isChange ? item.new : item.value, t)}${unit}`
}

export function formatLogItems(items: LogItem[], t: TFnType): string {
  return items.map(item => formatLogItem(item, t)).join(' • ')
}

function getVariant(log: EventLog): string | null {
  const details = log.details ?? {}
  if (details.variant) return details.variant
  if (log.event === 'PRESCRIPTION_STATUS_CHANGE') return details.action ?? null // old logs get the new names too
  if (log.event === 'PATIENT_UPDATED') return log.resource ?? null
  return null
}

/** t = useTranslate('common').t, tE = useTranslate('events').t */
export function getLogName(log: EventLog, t: TFnType, tE: TFnType): string {
  const key = getLogEvent(log)
  if (!log.event) return tE(`${key}.name`)
  const name = t(`event.name.${key}`)
  const variant = getVariant(log)
  return variant ? t({key: `event.variant.${log.event}.${variant}`, defaultValue: name}) : name
}

export function getLogMessage(log: EventLog, t: TFnType, tE: TFnType, {hidePatient = false} = {}): string {
  const key = getLogEvent(log)
  const details: Record<string, string | null | undefined> = {...log.details}

  // Cancel/reactivate/cycle-move logs written before they used `therapyInternalId` stored it as `id`.
  if (log.event === 'PATIENT_THERAPY_SEQUENCE_PROTOCOL_UPDATED' && !('therapyInternalId' in details)) {
    details.therapyInternalId = details.id
  }
  for (const k of [...COMMON_DEFAULTS, ...(MESSAGE_DEFAULTS[key] ?? [])]) details[k] ??= '-'
  if (log.event === 'PATIENT_DOSES_RECALCULATED' && details.cause) {
    details.cause = t(`dose_recalculation_cause.${details.cause}`)
  }
  if (details.tab) details.tab = t({key: `event.value.tab.${details.tab}`, defaultValue: details.tab})
  // Kept for message texts that still show the action; the name variant reads the code from log.details.
  if (log.event === 'PRESCRIPTION_STATUS_CHANGE' && details.action) {
    details.action = t({key: `actions.${details.action}`, ns: 'activity', defaultValue: details.action})
  }

  const storedItems = parseLogItems(log.details)
  const itemsText = formatLogItems([...storedItems, ...shownDetailItems(key, log.details)], t)

  let text = ''
  if (!(ITEMS_ONLY.has(key) && storedItems.length > 0)) {
    text = log.event ? t(`event.message.${key}`, details) : tE(`${key}.message`, details)
    if (log.event && hidePatient) text = t(`event.patient_message.${key}`, text, details)
  }
  return [text, itemsText].filter(part => part && part !== '-').join(' • ')
}
